import { getErrorMessageOrDefault, unknownToError, } from "@vortex/shared";
import * as React from "react";
import { Media } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { generate as shortid } from "shortid";
import { showDialog } from "../../actions/notifications";
import Dashlet from "../../controls/Dashlet";
import DynDiv from "../../controls/DynDiv";
import EmptyPlaceholder from "../../controls/EmptyPlaceholder";
import FlexLayout from "../../controls/FlexLayout";
import { log } from "../../util/log";
import { showError } from "../../util/message";
import { activeGameId } from "../../util/selectors";
import StarterInfo from "../../util/StarterInfo";
import { getSafe } from "../../util/storeHelper";
import { truthy } from "../../util/util";
import { MainContext } from "../../views/MainWindow";
import { addDiscoveredTool, setToolVisible, } from "../gamemode_management/actions/settings";
import { setPrimaryTool, setToolOrder } from "./actions";
import AddToolButton from "./AddToolButton";
import Tool from "./Tool";
import ToolEditDialog from "./ToolEditDialog";
import { propOf, updateJumpList } from "./util";
const initialState = {
    validToolIds: [],
    editTool: undefined,
    counter: 1,
    gameStarter: undefined,
    tools: [],
    discovering: false,
};
const welcomeScreenStateReducer = (state, actions) => {
    const newState = { ...state };
    for (const action of actions) {
        newState[action.type] = action.value;
    }
    return newState;
};
export default function Tools(props) {
    const [state, dispatch] = React.useReducer(welcomeScreenStateReducer, initialState);
    const { t } = useTranslation();
    const { onGetValidTools } = props;
    const context = React.useContext(MainContext);
    const { onShowError, onSetToolVisible, onSetPrimary, onSetToolOrder } = mapDispatchToProps(context.api.store.dispatch);
    const connectedProps = useSelector(mapStateToProps);
    const { discoveredGames, discoveredTools, gameMode, knownGames, toolsOrder, mods, primaryTool, deploymentCounter, } = connectedProps;
    const closeEditDialog = () => {
        dispatch([
            { type: propOf("editTool"), value: undefined },
            {
                type: propOf("counter"),
                value: state.counter + 1,
            },
        ]);
    };
    const applyOrder = React.useCallback((ordered) => {
        const names = ordered
            .map((id) => {
            const tool = state.tools.find((tool) => tool.id === id);
            return tool?.name;
        })
            .filter((name) => !!name);
        context.api.events.emit("analytics-track-event", "Tools", "Drag above/below", "Rearranged tools", names.join());
        onSetToolOrder(gameMode, ordered);
    }, [onSetToolOrder, gameMode, state.tools]);
    const addNewTool = () => {
        const game = knownGames.find((ele) => ele.id === gameMode);
        const empty = new StarterInfo(game, discoveredGames[gameMode], undefined, {
            id: shortid(),
            path: "",
            hidden: false,
            custom: true,
            workingDirectory: "",
            name: "",
            executable: undefined,
            requiredFiles: [],
            logo: undefined,
            shell: false,
        });
        dispatch([{ type: propOf("editTool"), value: empty }]);
        context.api.events.emit("analytics-track-click-event", "Tools", "Add tool");
    };
    const startTool = React.useCallback((info) => {
        if (info?.exePath === undefined) {
            onShowError("Tool missing/misconfigured", "Please ensure that the tool/game is configured correctly and try again", false);
            return;
        }
        context.api.events.emit("analytics-track-click-event", "Tools", "Manually ran tool");
        StarterInfo.run(info, context.api, onShowError);
    }, [onShowError]);
    const editTool = React.useCallback((starter) => {
        dispatch([
            { type: propOf("editTool"), value: starter },
        ]);
    }, []);
    const removeTool = React.useCallback((starter) => {
        context.api.events.emit("analytics-track-click-event", "Tools", "Removed tool");
        onSetToolVisible(starter.gameId, starter.id, false);
    }, [onSetToolVisible]);
    const setPrimary = React.useCallback((starter) => {
        if (starter.id === primaryTool) {
            onSetPrimary(starter.gameId, null);
        }
        else {
            context.api.events.emit("analytics-track-click-event", "Tools", "Selected new primary tool");
            onSetPrimary(starter.gameId, starter.isGame ? null : starter.id);
        }
    }, [onSetPrimary]);
    const onStateUpdate = () => {
        const gameStarter = generateGameStarter(connectedProps);
        const tools = generateToolStarters(connectedProps, gameStarter?.id);
        dispatch([
            { type: propOf("gameStarter"), value: gameStarter },
            { type: propOf("tools"), value: tools },
        ]);
        const jumpList = truthy(gameStarter) ? [gameStarter].concat(tools) : tools;
        updateJumpList(jumpList);
    };
    React.useEffect(() => {
        // One time deal.
        onStateUpdate();
    }, []);
    React.useEffect(() => {
        const updateValidTools = async (tools) => {
            const valid = await onGetValidTools(tools, gameMode);
            dispatch([{ type: "validToolIds", value: valid }]);
        };
        onStateUpdate();
        updateValidTools(state.tools);
    }, [
        discoveredGames,
        gameMode,
        knownGames,
        toolsOrder,
        mods,
        deploymentCounter,
    ]);
    React.useEffect(() => {
        if (toolsOrder.length === 0 && state.tools.length > 0) {
            applyOrder(state.tools.map((tool) => tool.id));
        }
    }, [state.tools, toolsOrder]);
    let content;
    const toolEditDialog = React.useCallback(() => state.editTool !== undefined ? (React.createElement(ToolEditDialog, { tool: state.editTool, onClose: closeEditDialog })) : null, [state.editTool, closeEditDialog]);
    if (gameMode === undefined) {
        content = (React.createElement(EmptyPlaceholder, { fill: true, icon: "game", text: t("When you are managing a game, supported tools will appear here") }));
    }
    else {
        const game = knownGames.find((ele) => ele.id === gameMode);
        const discoveredGame = discoveredGames[gameMode];
        content = (React.createElement(Media, { id: "starter-dashlet" },
            React.createElement(FlexLayout, { className: "starter-dashlet-tools-header", type: "row" },
                React.createElement("div", { className: "dashlet-title" }, t("Tools")),
                React.createElement(DynDiv, { group: "starter-dashlet-tools-controls" })),
            React.createElement(Media.Body, null,
                React.createElement(FlexLayout, { type: "column" },
                    toolEditDialog(),
                    React.createElement(ToolIcons, { addNewTool: addNewTool, applyOrder: applyOrder, counter: state.counter, discoveredGame: discoveredGame, discoveredTools: discoveredTools, editTool: editTool, game: game, removeTool: removeTool, setPrimary: setPrimary, startTool: startTool, tools: state.tools, validToolIds: state.validToolIds })))));
    }
    return (React.createElement(Dashlet, { className: "dashlet-starter", title: "" }, content));
}
function ToolIcons(props) {
    const { counter, discoveredTools, tools, discoveredGame, validToolIds, game, applyOrder, addNewTool, editTool, removeTool, setPrimary, startTool, } = props;
    if (game === undefined &&
        getSafe(discoveredGame, ["id"], undefined) === undefined) {
        return null;
    }
    if (tools === undefined) {
        return null;
    }
    const visible = tools.filter((starter) => starter.isGame ||
        discoveredTools[starter.id] === undefined ||
        discoveredTools[starter.id].hidden !== true);
    return (React.createElement("div", { className: "tool-icon-box" },
        visible.map((starter, idx) => (React.createElement(Tool, { applyOrder: applyOrder, counter: counter, editTool: editTool, idx: idx, key: starter.id + idx, removeTool: removeTool, setPrimary: setPrimary, starter: starter, startTool: startTool, tools: tools, validToolIds: validToolIds }))),
        React.createElement(AddToolButton, { tools: tools, onAddNewTool: addNewTool, onSetToolOrder: applyOrder })));
}
function generateGameStarter(props) {
    const { discoveredGames, gameMode, knownGames } = props;
    const game = knownGames.find((ele) => ele.id === gameMode);
    const discoveredGame = discoveredGames[gameMode];
    if (game === undefined || discoveredGame?.path === undefined) {
        return null;
    }
    try {
        const starter = new StarterInfo(game, discoveredGame);
        return starter;
    }
    catch (unknownError) {
        const err = unknownToError(unknownError);
        log("error", "failed to create dashlet game entry", {
            error: err.message,
            stack: err.stack,
        });
    }
    return null;
}
function generateToolStarters(props, gameStarterId) {
    const { discoveredGames, discoveredTools, gameMode, knownGames, toolsOrder } = props;
    const game = knownGames.find((ele) => ele.id === gameMode);
    const discoveredGame = discoveredGames[gameMode];
    if (game === undefined || discoveredGame?.path === undefined) {
        return [];
    }
    const knownTools = getSafe(game, ["supportedTools"], []);
    const gameId = discoveredGame.id || game.id;
    const preConfTools = new Set(knownTools.map((tool) => tool.id));
    const starters = [];
    // add the tools provided by the game extension (whether they are found or not)
    knownTools.forEach((tool) => {
        try {
            starters.push(new StarterInfo(game, discoveredGame, tool, discoveredTools[tool.id]));
        }
        catch (err) {
            log("warn", "invalid tool", { err });
        }
    });
    // finally, add those tools that were added manually
    Object.keys(discoveredTools)
        .filter((toolId) => !preConfTools.has(toolId) && toolId !== gameStarterId)
        .sort((lhs, rhs) => {
        const tlhs = discoveredTools[lhs]?.timestamp || 0;
        const trhs = discoveredTools[rhs]?.timestamp || 0;
        return tlhs - trhs;
    })
        .forEach((toolId) => {
        try {
            starters.push(new StarterInfo(game, discoveredGame, undefined, discoveredTools[toolId]));
        }
        catch (err) {
            log("error", "tool configuration invalid", {
                gameId,
                toolId,
                error: getErrorMessageOrDefault(err),
            });
        }
    });
    const findIdx = (starter) => {
        const idx = toolsOrder.findIndex((toolId) => toolId === starter.id);
        return idx !== -1 ? idx : starters.length;
    };
    starters.sort((lhs, rhs) => findIdx(lhs) - findIdx(rhs));
    return starters;
}
const emptyObj = {};
const emptyArray = [];
let lastConnected;
function mapStateToProps(state) {
    const gameMode = activeGameId(state);
    const res = {
        gameMode,
        addToTitleBar: getSafe(state, ["settings", "interface", "tools", "addToolsToTitleBar"], false),
        toolsOrder: getSafe(state, ["settings", "interface", "tools", "order", gameMode], emptyArray),
        knownGames: state.session.gameMode.known,
        discoveredGames: state.settings.gameMode.discovered,
        discoveredTools: getSafe(state, ["settings", "gameMode", "discovered", gameMode, "tools"], emptyObj),
        primaryTool: getSafe(state, ["settings", "interface", "primaryTool", gameMode], undefined),
        toolsRunning: state.session.base.toolsRunning,
        mods: getSafe(state, ["persistent", "mods", gameMode], emptyObj),
        deploymentCounter: state.persistent?.deployment?.deploymentCounter?.[gameMode] ?? 0,
    };
    const keys = Object.keys(res);
    if (lastConnected === undefined ||
        keys.find((key) => res[key] !== lastConnected[key]) !== undefined) {
        lastConnected = res;
    }
    return lastConnected;
}
function mapDispatchToProps(dispatch) {
    return {
        onAddDiscoveredTool: (gameId, toolId, result) => {
            dispatch(addDiscoveredTool(gameId, toolId, result, true));
        },
        onSetToolVisible: (gameId, toolId, visible) => {
            dispatch(setToolVisible(gameId, toolId, visible));
        },
        onShowError: (message, details, allowReport) => showError(dispatch, message, details, { allowReport }),
        onShowDialog: (type, title, content, actions) => dispatch(showDialog(type, title, content, actions)),
        onSetPrimary: (gameId, toolId) => dispatch(setPrimaryTool(gameId, toolId)),
        onSetToolOrder: (gameId, order) => dispatch(setToolOrder(gameId, order)),
    };
}
